import type { PortalFlowQuote } from '../types/portal'
import { isPortalUuid } from './portalSafety'

function requireValid(value: unknown): asserts value {
  if (!value) throw new Error('PORTAL_INVALID_RESPONSE')
}

function record(value: unknown): Record<string, unknown> {
  requireValid(value && typeof value === 'object' && !Array.isArray(value))
  return value as Record<string, unknown>
}

// Validate representation/bounds only. Pricing and commissions remain server-owned.
function decimal(value: unknown, scale: number, positive = false): value is string {
  if (typeof value !== 'string' || value.length > 55 ||
      !new RegExp(`^(0|[1-9][0-9]*)(\\.[0-9]{1,${scale}})?$`).test(value)) return false
  const [whole, fraction = ''] = value.split('.')
  const minor = (whole + fraction.padEnd(scale, '0')).replace(/^0+/, '') || '0'
  return minor.length <= 36 && (!positive || minor !== '0')
}

function minor(value: string, scale: number): string {
  const [whole, fraction = ''] = value.split('.')
  return (whole + fraction.padEnd(scale, '0')).replace(/^0+/, '') || '0'
}

export function validatePortalFlowTypes(types: unknown): asserts types is string[] {
  if (!Array.isArray(types) || types.length === 0 || types.length > 12 ||
      !types.every(type => typeof type === 'string' && /^gonka-game_f(?:[1-9]|1[0-2])$/.test(type)) ||
      new Set(types).size !== types.length) throw new Error('PORTAL_INVALID_REQUEST')
}

export function parsePortalFlowQuote(value: unknown, requested: string[]): PortalFlowQuote {
  const quote = record(value)
  requireValid(isPortalUuid(quote.quote_id) && typeof quote.expires_at === 'number' &&
    Number.isSafeInteger(quote.expires_at) && quote.expires_at > 0 && quote.expires_at <= 8640000000000)
  const snapshot = record(quote.snapshot)
  requireValid(Array.isArray(snapshot.types) && snapshot.types.length === requested.length &&
    new Set(snapshot.types).size === requested.length && snapshot.types.every(type => requested.includes(type)))
  const types = snapshot.types as string[]
  requireValid(Array.isArray(snapshot.items) && snapshot.items.length === types.length)
  snapshot.items.forEach((value, index) => {
    const item = record(value)
    requireValid(item.type === types[index] && decimal(item.gnk_amount, 9, true))
  })
  for (const key of ['gnk_target_minor', 'swap_usdt_minor', 'invoice_usdt_minor', 'amount_rub_minor',
    'rounding_surplus_usdt_minor', 'gas_estimate', 'swap_pool_fee']) {
    requireValid(typeof snapshot[key] === 'string' && /^(0|[1-9][0-9]{0,35})$/.test(snapshot[key] as string))
  }
  for (const key of ['gnk_target_minor', 'swap_usdt_minor', 'invoice_usdt_minor', 'amount_rub_minor', 'swap_pool_fee'])
    requireValid(snapshot[key] !== '0')
  requireValid(decimal(snapshot.gnk_amount, 9, true) && decimal(snapshot.usdt_amount, 6, true) &&
    decimal(snapshot.rub_amount, 2, true) && decimal(snapshot.rate_rub, 18, true) &&
    decimal(snapshot.min_rub, 2) && decimal(snapshot.max_rub, 2, true) &&
    typeof snapshot.kyc_required === 'boolean' &&
    typeof snapshot.swap_quote_source === 'string' &&
    ['gonka-rate-service', 'uniswap-v3-quoter'].includes(snapshot.swap_quote_source))
  // Cross-check duplicate representations and limits, not a frontend quote calculation.
  requireValid(minor(snapshot.gnk_amount as string, 9) === snapshot.gnk_target_minor &&
    minor(snapshot.usdt_amount as string, 6) === snapshot.invoice_usdt_minor &&
    minor(snapshot.rub_amount as string, 2) === snapshot.amount_rub_minor &&
    BigInt(snapshot.amount_rub_minor as string) >= BigInt(minor(snapshot.min_rub as string, 2)) &&
    BigInt(snapshot.amount_rub_minor as string) <= BigInt(minor(snapshot.max_rub as string, 2)))
  requireValid(Array.isArray(snapshot.fees))
  const codes = new Set<string>()
  snapshot.fees.forEach(value => {
    const fee = record(value)
    requireValid(typeof fee.code === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(fee.code) &&
      !codes.has(fee.code) && fee.currency === 'USDT' && decimal(fee.amount, 6))
    codes.add(fee.code)
  })
  return quote as unknown as PortalFlowQuote
}
